"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import CountryPicker from "@/components/CountryPicker";
import { classifyCountryForSignupProviders } from "@/lib/auth/country-classification";

type AccountType = "individual" | "company";

interface SelectedCountry {
  id: string;
  name: string;
  country_code: string;
}

function ProviderButton({
  provider,
  accountType,
  regionId,
  displayName,
  children,
}: {
  provider: "google" | "microsoft";
  accountType: AccountType;
  regionId?: string;
  displayName?: string;
  children: React.ReactNode;
}) {
  const params = new URLSearchParams({ account_type: accountType });
  if (regionId) params.set("region_id", regionId);
  if (displayName) params.set("display_name", displayName);

  return (
    <a
      href={`/api/auth/${provider}/start?${params.toString()}`}
      className="flex w-full items-center justify-center rounded-lg border border-zinc-300 px-4 py-3 text-sm font-medium text-zinc-900 hover:bg-zinc-50 dark:border-zinc-700 dark:text-zinc-50 dark:hover:bg-zinc-900"
    >
      {children}
    </a>
  );
}

function DisabledProviderButton({ children, comingSoon }: { children: React.ReactNode; comingSoon: string }) {
  return (
    <div className="flex w-full cursor-not-allowed items-center justify-center rounded-lg border border-dashed border-zinc-300 px-4 py-3 text-sm text-zinc-400 dark:border-zinc-700 dark:text-zinc-600">
      {children} — {comingSoon}
    </div>
  );
}

export default function SignupPage() {
  const t = useTranslations("signup");
  const tc = useTranslations("common");
  const [accountType, setAccountType] = useState<AccountType | null>(null);
  const [country, setCountry] = useState<SelectedCountry | null>(null);
  const [displayName, setDisplayName] = useState("");

  const bucket = country ? classifyCountryForSignupProviders(country.country_code) : null;
  const trimmedName = displayName.trim();

  return (
    <div className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center gap-8 px-6 py-16">
      <h1 className="text-2xl font-semibold text-zinc-900 dark:text-zinc-50">{t("title")}</h1>

      {!accountType && (
        <div className="flex flex-col gap-3">
          <p className="text-sm text-zinc-600 dark:text-zinc-400">{t("chooseAccountType")}</p>

          <button
            onClick={() => setAccountType("individual")}
            className="flex flex-col gap-1 rounded-lg bg-zinc-900 px-4 py-3 text-left text-white dark:bg-zinc-50 dark:text-zinc-900"
          >
            <span className="text-sm font-medium">{t("individual")}</span>
            <span className="text-xs text-zinc-300 dark:text-zinc-600">{t("individualDesc")}</span>
          </button>

          <button
            onClick={() => setAccountType("company")}
            className="flex flex-col gap-1 rounded-lg border border-zinc-300 px-4 py-3 text-left text-zinc-900 dark:border-zinc-700 dark:text-zinc-50"
          >
            <span className="text-sm font-medium">{t("company")}</span>
            <span className="text-xs text-zinc-600 dark:text-zinc-400">{t("companyDesc")}</span>
          </button>

          <p className="text-xs text-zinc-500 dark:text-zinc-500">{t("accountTypeUpgradeNote")}</p>
        </div>
      )}

      {accountType === "individual" && (
        <div className="flex flex-col gap-3">
          <p className="text-sm text-zinc-600 dark:text-zinc-400">{t("socialLoginNote")}</p>
          <ProviderButton provider="google" accountType="individual">
            {t("signupWithGoogle")}
          </ProviderButton>
          {/* Microsoft OAuth 앱 미등록 — 요구가 들어오면 다시 넣는다(사용자 결정) */}
          <button onClick={() => setAccountType(null)} className="text-sm text-zinc-500 underline">
            {tc("back")}
          </button>
        </div>
      )}

      {accountType === "company" && !country && (
        <div className="flex flex-col gap-3">
          <p className="text-sm text-zinc-600 dark:text-zinc-400">{t("chooseCountryPrompt")}</p>
          <CountryPicker onSelect={(c) => setCountry(c)} />
          <button onClick={() => setAccountType(null)} className="text-sm text-zinc-500 underline">
            {tc("back")}
          </button>
        </div>
      )}

      {accountType === "company" && country && (
        <div className="flex flex-col gap-3">
          <label className="text-sm font-medium text-zinc-700 dark:text-zinc-300">{t("companyNameLabel")}</label>
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            maxLength={200}
            placeholder={t("companyNamePlaceholder")}
            className="rounded-lg border border-zinc-300 px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-950"
          />

          {trimmedName && (
            <>
              <p className="text-sm text-zinc-600 dark:text-zinc-400">{t("companyEmailNote")}</p>

              {bucket === "default" && (
                <>
                  <ProviderButton provider="google" accountType="company" regionId={country.id} displayName={trimmedName}>
                    {t("signupWithGoogleWorkspace")}
                  </ProviderButton>
                  {/* Microsoft OAuth 앱 미등록 — 요구가 들어오면 다시 넣는다(사용자 결정) */}
                </>
              )}

              {bucket === "china" && (
                <>
                  <DisabledProviderButton comingSoon={t("comingSoon")}>{t("wecom")}</DisabledProviderButton>
                  <DisabledProviderButton comingSoon={t("comingSoon")}>{t("alipay")}</DisabledProviderButton>
                </>
              )}

              {bucket === "eu" && (
                <DisabledProviderButton comingSoon={t("comingSoon")}>{t("eudiWallet")}</DisabledProviderButton>
              )}
            </>
          )}

          <button onClick={() => setCountry(null)} className="text-sm text-zinc-500 underline">
            {tc("back")}
          </button>
        </div>
      )}
    </div>
  );
}
